import { gql, useMutation, useQuery } from '@apollo/client'

import {
  CreateArtistThreadChannel,
  CreateArtistThreadChannelMutation,
  CreateArtistThreadChannelMutationVariables,
  ThreadBoxFragmentDoc,
  ThreadEditQuery,
  ThreadEditQueryVariables,
  ThreadStatus,
  UpdateArtistThreadChannelMutation,
  UpdateArtistThreadChannelMutationVariables,
} from '@/clients/utoniq-core/schema'
import { config } from '@/config'
import { useRouter } from '@/core'
import { makeViewModel } from '@/core/architecture'
import { useCurrentUser } from '@/core/auth'

import { Action, State } from './template'

export const useViewModel = makeViewModel<State, Action>(() => {
  const { user } = useCurrentUser()
  const { router } = useRouter<{ threadId: string; artistId: string }>()
  const { data } = useQuery<ThreadEditQuery, ThreadEditQueryVariables>(QUERY, {
    variables: {
      artistThreadChannelId: router.query.threadId,
      artistId: router.query.artistId ?? config.artistId,
    },
  })

  const [createArtistThreadChannel] = useMutation<
    CreateArtistThreadChannelMutation,
    CreateArtistThreadChannelMutationVariables
  >(CreateArtistThreadChannel)

  const [updateArtistThreadChannel] = useMutation<
    UpdateArtistThreadChannelMutation,
    UpdateArtistThreadChannelMutationVariables
  >(UPDATE_ARTIST_THREAD_CHANNEL)

  return {
    state: {
      thread: data?.artistThreadChannel ?? null,
      artist: data?.artist ?? null,
      user: data?.user ?? null,
    },
    actions: {
      async onEditThread({ thumbnail, ...inputs }) {
        const { name, description, category } = inputs
        await updateArtistThreadChannel({
          variables: {
            updateArtistThreadChannelId: router.query?.threadId,
            inputs: {
              name,
              description,
              category,
              status: ThreadStatus.Published,
              thumbnail: thumbnail?.tmpPath
                ? { tmpPath: thumbnail.tmpPath }
                : undefined,
            },
          },
        })
      },
      async onSaveDraft({ thumbnail, ...inputs }) {
        await createArtistThreadChannel({
          variables: {
            inputs: {
              ...inputs,
              status: ThreadStatus.Draft,
              artistId: user?.uid ?? '',
              thumbnail: thumbnail?.tmpPath
                ? { tmpPath: thumbnail.tmpPath }
                : undefined,
            },
          },
        })
      },
    },
  }
})

const QUERY = gql`
  query ThreadEdit($artistThreadChannelId: ID!, $artistId: ID) {
    artistThreadChannel(id: $artistThreadChannelId) {
      ...ThreadBox
    }
    artist(id: $artistId) {
      id
      displayName
      avatar {
        uri
      }
      patron {
        id
      }
    }
    user {
      id
      avatar {
        uri
      }
      displayName
    }
  }
  ${ThreadBoxFragmentDoc}
`

const UPDATE_ARTIST_THREAD_CHANNEL = gql`
  mutation UpdateArtistThreadChannel(
    $updateArtistThreadChannelId: ID!
    $inputs: UpdateArtistThreadInput!
  ) {
    updateArtistThreadChannel(
      id: $updateArtistThreadChannelId
      inputs: $inputs
    ) {
      ...ThreadBox
    }
  }
  ${ThreadBoxFragmentDoc}
`
